import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Coffee, ShoppingCart, Factory, HeartPulse, Utensils, SprayCan, HardHat, Baby, Boxes, CheckCircle } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { ProductCard } from "../components/ProductCard";
import { SEO } from "../components/SEO";
import { getIndustry } from "../lib/api";

const ICON = {
  coffee: Coffee, "shopping-cart": ShoppingCart, factory: Factory, "hard-hat": HardHat,
  "heart-pulse": HeartPulse, utensils: Utensils, "spray-can": SprayCan, baby: Baby,
};

export default function IndustryDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setData(null); setNotFound(false);
    getIndustry(slug).then(setData).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main data-testid="industry-notfound" className="min-h-screen grid place-items-center pt-40">
        <div className="text-center">
          <h1 className="font-display font-bold text-4xl text-vr-ink mb-4">Industry not found</h1>
          <Link to="/industries" className="text-vr-brand font-semibold underline">All industries</Link>
        </div>
      </main>
    );
  }
  if (!data) return <div className="min-h-screen grid place-items-center text-vr-dim pt-40">Loading…</div>;

  const { industry, essentials, products, product_total } = data;
  const Icon = ICON[industry.icon] || Boxes;

  return (
    <main data-testid="industry-detail-page" className="pt-[104px] bg-vr-bg">
      <SEO
        title={`${industry.name} — Wholesale Supplies | VR Packaging`}
        description={industry.intro || industry.desc}
        path={`/industries/${industry.slug}`}
        image={industry.image}
      />

      {/* Editorial hero */}
      <section className="relative overflow-hidden bg-vr-navy text-white">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-16 md:py-24 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <Breadcrumbs items={[{ label: "Industries", to: "/industries" }, { label: industry.name }]} />
            <div className="mt-6 flex items-center gap-3 text-vr-hygiene text-xs uppercase tracking-[0.2em] font-semibold">
              <span className="grid place-items-center w-10 h-10 rounded-xl bg-white/10"><Icon size={20} /></span>
              For {industry.name}
            </div>
            <h1 className="mt-6 font-display font-extrabold tracking-tight text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
              {industry.headline || `Wholesale supplies for ${industry.name.toLowerCase()}.`}
            </h1>
            <p className="mt-6 text-white/75 text-lg leading-relaxed max-w-xl">{industry.intro || industry.desc}</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/trade-account" data-testid="ind-trade-cta" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-vr-brand text-white font-semibold hover:bg-vr-branddark transition-colors">
                Open a Trade Account <ArrowRight size={17} />
              </Link>
              <Link to="/contact" data-testid="ind-quote-cta" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors">
                Request a Quote
              </Link>
            </div>
          </div>
          {industry.image && (
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="relative aspect-[4/3] rounded-3xl overflow-hidden border border-white/10 shadow-2xl"
            >
              <img src={industry.image} alt={industry.name} className="w-full h-full object-cover" />
            </motion.div>
          )}
        </div>
        <div className="absolute -top-24 -right-16 w-80 h-80 rounded-full bg-vr-brand/30 blur-3xl pointer-events-none" />
      </section>

      {/* Essentials */}
      {essentials?.length > 0 && (
        <section data-testid="industry-essentials" className="py-20 md:py-24">
          <div className="max-w-[1500px] mx-auto px-6 md:px-10">
            <span className="text-xs tracking-[0.2em] uppercase text-vr-brand font-bold">Essentials for this industry</span>
            <h2 className="mt-3 font-display font-extrabold tracking-tight text-3xl md:text-4xl text-vr-ink max-w-2xl">
              Everything a {industry.name.toLowerCase().replace(/&/g, "and")} operation needs, on one account.
            </h2>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {essentials.map((e, i) => (
                <motion.div
                  key={e.title + i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.45, delay: (i % 3) * 0.05 }}
                >
                  <Link
                    to={e.href}
                    data-testid={`essential-${i}`}
                    className="group flex items-center gap-4 rounded-2xl border border-vr-border bg-white p-4 hover:border-vr-brand hover:shadow-lg transition-all"
                  >
                    <div className="w-20 h-20 rounded-xl overflow-hidden bg-[#e6f4fc] shrink-0">
                      {e.image && <img src={e.image} alt={e.title} loading="lazy" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] uppercase tracking-wider text-vr-brand font-bold">Essential</div>
                      <div className="mt-1 font-display font-bold text-vr-ink group-hover:text-vr-brand transition-colors line-clamp-1">{e.title}</div>
                      {e.count > 0 && <div className="mt-1 text-xs text-vr-dim">{e.count} product{e.count === 1 ? "" : "s"}</div>}
                    </div>
                    <ArrowRight size={18} className="text-vr-dim group-hover:text-vr-brand group-hover:translate-x-1 transition-all shrink-0" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Curated product picks */}
      {products?.length > 0 && (
        <section data-testid="industry-products" className="pb-20 md:pb-28 bg-vr-bg2">
          <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-20">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-10">
              <div>
                <span className="text-xs tracking-[0.2em] uppercase text-vr-brand font-bold">Curated for {industry.name}</span>
                <h2 className="mt-3 font-display font-extrabold tracking-tight text-3xl md:text-4xl text-vr-ink max-w-2xl">
                  Top picks our {industry.name.toLowerCase()} customers order most.
                </h2>
              </div>
              {product_total > products.length && (
                <span className="text-sm text-vr-dim">Showing {products.length} of {product_total} relevant products</span>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {products.map((p, i) => <ProductCard key={p.sku} p={p} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* Why VR for this industry */}
      <section data-testid="industry-why" className="py-20 md:py-28">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="text-xs tracking-[0.2em] uppercase text-vr-brand font-bold">The VR promise</span>
            <h2 className="mt-3 font-display font-extrabold tracking-tight text-3xl md:text-4xl text-vr-ink">
              Built for {industry.name.toLowerCase()} — priced for wholesale.
            </h2>
            <ul className="mt-8 space-y-4">
              {[
                "One dependable supplier — no more juggling accounts.",
                "Wholesale pricing that sharpens as your volume grows.",
                "Nationwide dispatch with account-priority scheduling.",
                "Real people who understand your industry's supply needs.",
              ].map((line, i) => (
                <li key={i} className="flex items-start gap-3 text-vr-ink">
                  <CheckCircle size={20} className="text-vr-brand mt-0.5 shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl bg-gradient-to-br from-vr-navy via-[#12405b] to-vr-brand p-10 md:p-12 text-white relative overflow-hidden">
            <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
            <h3 className="relative font-display font-extrabold text-2xl md:text-3xl">Ready to simplify {industry.name.toLowerCase()} supply?</h3>
            <p className="relative mt-4 text-white/80">Open a trade account and get pricing tailored to your operation — usually within one business day.</p>
            <div className="relative mt-8 flex flex-wrap gap-3">
              <Link to="/trade-account" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-vr-navy font-semibold hover:bg-vr-hygiene transition-colors">
                Apply now <ArrowRight size={17} />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/40 text-white font-semibold hover:bg-white/10 transition-colors">
                Request a Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-[1500px] mx-auto px-6 md:px-10 pb-16">
        <Link to="/industries" className="inline-flex items-center gap-2 text-vr-navy font-semibold hover:text-vr-brand transition-colors">
          <ArrowLeft size={16} /> Back to all industries
        </Link>
      </div>
    </main>
  );
}
